import React, { useState } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  IconButton,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { resumeService } from '../../../services/resume';

const ExperienceForm = ({ resumeId, onError }) => {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Experience form
  const [experienceForm, setExperienceForm] = useState({
    position: '',
    companyName: '',
    location: '',
    startDate: '',
    endDate: '',
    current: false,
    description: ''
  });

  const handleExperienceChange = (field, value) => {
    setExperienceForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addExperience = async () => {
    if (!experienceForm.position || !experienceForm.companyName) {
      setError('Please fill in at least the job title and company.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const experienceData = {
        position: experienceForm.position,
        companyName: experienceForm.companyName,
        location: experienceForm.location,
        startDate: experienceForm.startDate,
        endDate: experienceForm.current ? null : experienceForm.endDate,
        description: experienceForm.description
      };

      const response = await resumeService.createExperience(resumeId, experienceData);
      
      // Add the new experience to the local state
      setExperiences(prev => [...prev, { ...experienceData, id: response.id }]);
      
      // Reset form
      setExperienceForm({
        position: '',
        companyName: '',
        location: '',
        startDate: '',
        endDate: '',
        current: false,
        description: ''
      });

      setSuccess('Experience added successfully!');
      setTimeout(() => setSuccess(''), 3000);

    } catch (error) {
      const errorMessage = `Failed to add experience: ${error.message}`;
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const removeExperience = async (experienceId) => {
    try {
      await resumeService.deleteExperience(resumeId, experienceId);
      setExperiences(prev => prev.filter(exp => exp.id !== experienceId));
      setSuccess('Experience removed successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      const errorMessage = `Failed to remove experience: ${error.message}`;
      setError(errorMessage);
      if (onError) onError(errorMessage);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Work Experience</Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      {/* Add Experience Form */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Add New Experience</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Job Title"
              value={experienceForm.position}
              onChange={(e) => handleExperienceChange('position', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Company"
              value={experienceForm.companyName}
              onChange={(e) => handleExperienceChange('companyName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Location"
              value={experienceForm.location}
              onChange={(e) => handleExperienceChange('location', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={experienceForm.startDate}
              onChange={(e) => handleExperienceChange('startDate', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={experienceForm.endDate}
              onChange={(e) => handleExperienceChange('endDate', e.target.value)}
              disabled={experienceForm.current}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={experienceForm.description}
              onChange={(e) => handleExperienceChange('description', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addExperience}
              disabled={loading || !experienceForm.position || !experienceForm.companyName}
            >
              {loading ? 'Adding...' : 'Add Experience'}
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Existing Experiences */}
      {experiences.map((exp) => (
        <Card key={exp.id} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
              <Typography variant="h6">{exp.position}</Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {exp.companyName} • {exp.location}
              </Typography>
                <Typography variant="body2" color="text.secondary">
                  {exp.startDate} - {exp.current ? 'Present' : exp.endDate}
                </Typography>
                {exp.description && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {exp.description}
                  </Typography>
                )}
              </Box>
              <IconButton
                color="error"
                onClick={() => removeExperience(exp.id)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Paper>
  );
};

export default ExperienceForm; 